import { Component, Input, Output, EventEmitter, AfterViewInit, OnInit, OnChanges, SimpleChanges, ElementRef, Renderer2 as Renderer } from '@angular/core';
import { ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import { Observable, of, BehaviorSubject } from 'rxjs';

import { SessionSummary } from '../../upgrade-logs-viewer.model';
import { UpgradeLogsFilterService } from '../../services/upgrade-logs-filter.service';
import { CreateCustomBlockSizeType, ICustomBlock } from '../../../decorators';

@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'lib-session-list',
  templateUrl: './session-list.component.html',
  styleUrls: ['./session-list.component.scss']
})
export class SessionListComponent implements AfterViewInit, OnInit, OnChanges, ICustomBlock {
  @Input() sessions: SessionSummary[] | null = [];
  @Input() loading = false;
  @Input() selectedSession: SessionSummary | null = null;

  private _labels: any = {};
  @Input() set labels(value: any) {
    this._labels = value;
    this.updateColumnLabels();
  }
  get labels(): any {
    return this._labels;
  }

  @Output() sessionSelected = new EventEmitter<SessionSummary>();
  @Output() exportSession = new EventEmitter<SessionSummary>();
  @Output() exportSgpCommands = new EventEmitter<SessionSummary>();

  columns: ColumnConfig[] = [];

  // Configurazione paginazione seguendo il pattern di device-registry
  defaultPaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
    length: 0,
  };

  paginationConfig = this.defaultPaginationConfig;

  // Observable per i dati seguendo il pattern di device-registry
  private sessionsSubject = new BehaviorSubject<SessionSummary[]>([]);
  data$ = this.sessionsSubject.asObservable();

  // ICustomBlock properties
  width?: number;
  currentWidth?: number;

  constructor(
    private filterService: UpgradeLogsFilterService,
    public el: ElementRef,
    public renderer: Renderer
  ) {}

  ngOnInit() {
    this.updateColumnLabels();
    this.updateData();
  }

  ngAfterViewInit() {
    this.updateColumnLabels();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sessions']) {
      this.updateData();
    }
  }

  private updateData() {
    const sessions = this.sessions || [];
    this.sessionsSubject.next(sessions);
    this.paginationConfig = {
      ...this.defaultPaginationConfig,
      length: sessions.length
    };
  }

  // Inizializzazione colonne seguendo il pattern di alert-registry
  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'deviceSerial',
        header: this.labels.deviceSerial || 'Device Serial',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (session: SessionSummary) => session.deviceSerial
      },
      {
        columnDef: 'deviceModel',
        header: this.labels.deviceModel || 'Model',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (session: SessionSummary) => session.deviceModel
      },
      {
        columnDef: 'operatorName',
        header: this.labels.operator || 'Operator',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (session: SessionSummary) => session.operatorName
      },
      {
        columnDef: 'startTime',
        header: this.labels.startTime || 'Start Time',
        type: ColumnTypeEnum.Date,
        sortable: true,
        cell: (session: SessionSummary) => new Date(session.startTime),
        format: 'dd/MM/yyyy HH:mm'
      },
      {
        columnDef: 'duration',
        header: this.labels.duration || 'Duration',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (session: SessionSummary) => this.filterService.formatDuration(session.duration)
      },
      {
        columnDef: 'modules',
        header: this.labels.modules || 'Modules',
        type: ColumnTypeEnum.Text,
        cell: (session: SessionSummary) => this.formatModules(session.modules)
      }
    ];
  }

  // Event handlers
  onSessionSelected(session: SessionSummary): void {
    this.sessionSelected.emit(session);
  }

  // Helper methods

  private formatModules(modules: string[]): string {
    if (!modules?.length) return 'N/A';
    return modules.join(', ');
  }
}